import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/ui/Button';
import PackageTable from './components/PackageTable';
import PackageCreationWizard from './components/PackageCreationWizard';
import AnalyticsSidebar from './components/AnalyticsSidebar';
import PackageQuickStats from './components/PackageQuickStats';

const AdminPackageManagement = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock data for packages
  const mockPackages = [
    {
      id: '1',
      title: 'Ooty Hill Station Delight',
      destination: 'Ooty, Tamil Nadu',
      category: 'Hill Station',
      duration: '3 Days 2 Nights',
      price: '₹12,500',
      status: 'active',
      bookings: 145,
      rating: 4.6,
      lastModified: '14/07/2025',
      image: 'https://images.pexels.com/photos/3225517/pexels-photo-3225517.jpeg',
      description: 'Experience the Queen of Hill Stations with scenic tea gardens, pleasant weather, and colonial charm.',
      highlights: ['Botanical Gardens', 'Toy Train Ride', 'Tea Factory Visit', 'Doddabetta Peak'],
      inclusions: ['Accommodation', 'Breakfast', 'Transportation', 'Sightseeing'],
      exclusions: ['Lunch & Dinner', 'Personal Expenses', 'Entry Fees'],
      itinerary: [
        {
          day: 1,
          title: 'Arrival and Local Sightseeing',
          activities: ['Check-in to hotel', 'Visit Botanical Gardens', 'Evening at Ooty Lake']
        },
        {
          day: 2,
          title: 'Tea Gardens and Doddabetta Peak',
          activities: ['Tea Factory Visit', 'Doddabetta Peak', 'Rose Garden']
        }
      ],
      images: ['https://images.pexels.com/photos/3225517/pexels-photo-3225517.jpeg']
    },
    {
      id: '2',
      title: 'Kodaikanal Romantic Getaway',
      destination: 'Kodaikanal, Tamil Nadu',
      category: 'Hill Station',
      duration: '4 Days 3 Nights',
      price: '₹15,800',
      status: 'active',
      bookings: 132,
      rating: 4.5,
      lastModified: '13/07/2025',
      image: 'https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg',
      description: 'Discover the Princess of Hill Stations with misty mountains, serene lakes, and romantic ambiance.',
      highlights: ['Kodai Lake', 'Coaker\'s Walk', 'Bryant Park', 'Pillar Rocks'],
      inclusions: ['Accommodation', 'Breakfast', 'Transportation', 'Sightseeing'],
      exclusions: ['Lunch & Dinner', 'Personal Expenses', 'Entry Fees'],
      itinerary: [
        {
          day: 1,
          title: 'Arrival and Lake Activities',
          activities: ['Check-in to hotel', 'Kodai Lake boating', 'Evening walk']
        }
      ],
      images: ['https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg']
    },
    {
      id: '3',
      title: 'Munnar Tea Paradise',
      destination: 'Munnar, Kerala',
      category: 'Hill Station',
      duration: '3 Days 2 Nights',
      price: '₹14,200',
      status: 'suspended',
      bookings: 98,
      rating: 4.7,
      lastModified: '12/07/2025',
      image: 'https://images.pexels.com/photos/962464/pexels-photo-962464.jpeg',
      description: 'Explore the tea capital of South India with rolling hills, spice plantations, and cool climate.',
      highlights: ['Tea Plantations', 'Mattupetty Dam', 'Echo Point', 'Top Station'],
      inclusions: ['Accommodation', 'Breakfast', 'Transportation', 'Sightseeing'],
      exclusions: ['Lunch & Dinner', 'Personal Expenses', 'Entry Fees'],
      itinerary: [
        {
          day: 1,
          title: 'Tea Gardens and Mattupetty',
          activities: ['Tea plantation visit', 'Mattupetty Dam', 'Echo Point']
        }
      ],
      images: ['https://images.pexels.com/photos/962464/pexels-photo-962464.jpeg']
    },
    {
      id: '4',
      title: 'Goa Beach Carnival',
      destination: 'Goa',
      category: 'Beach',
      duration: '5 Days 4 Nights',
      price: '₹18,500',
      status: 'active',
      bookings: 87,
      rating: 4.4,
      lastModified: '11/07/2025',
      image: 'https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg',
      description: 'Experience the vibrant beach culture with golden sands, water sports, and nightlife.',
      highlights: ['Baga Beach', 'Water Sports', 'Spice Plantation', 'Dudhsagar Falls'],
      inclusions: ['Accommodation', 'Breakfast', 'Transportation', 'Sightseeing'],
      exclusions: ['Lunch & Dinner', 'Personal Expenses', 'Entry Fees'],
      itinerary: [
        {
          day: 1,
          title: 'Beach Day and Water Sports',
          activities: ['Baga Beach', 'Water sports', 'Beach shacks']
        }
      ],
      images: ['https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg']
    },
    {
      id: '5',
      title: 'Coorg Coffee Trail',
      destination: 'Coorg, Karnataka',
      category: 'Hill Station',
      duration: '3 Days 2 Nights',
      price: '₹13,800',
      status: 'draft',
      bookings: 0,
      rating: 0,
      lastModified: '10/07/2025',
      image: 'https://images.pexels.com/photos/1459505/pexels-photo-1459505.jpeg',
      description: 'Discover the Scotland of India with coffee plantations, misty hills, and rich culture.',
      highlights: ['Coffee Plantations', 'Abbey Falls', 'Raja\'s Seat', 'Dubare Elephant Camp'],
      inclusions: ['Accommodation', 'Breakfast', 'Transportation', 'Sightseeing'],
      exclusions: ['Lunch & Dinner', 'Personal Expenses', 'Entry Fees'],
      itinerary: [
        {
          day: 1,
          title: 'Coffee Estate and Abbey Falls',
          activities: ['Coffee plantation tour', 'Abbey Falls', 'Local market']
        }
      ],
      images: ['https://images.pexels.com/photos/1459505/pexels-photo-1459505.jpeg']
    },
    {
      id: '6',
      title: 'Rameswaram Spiritual Journey',
      destination: 'Rameswaram, Tamil Nadu',
      category: 'Religious',
      duration: '2 Days 1 Night',
      price: '₹8,500',
      status: 'active',
      bookings: 76,
      rating: 4.3,
      lastModified: '09/07/2025',
      image: 'https://images.pexels.com/photos/3408744/pexels-photo-3408744.jpeg',
      description: 'Embark on a sacred pilgrimage to one of the holiest places in Hindu tradition.',
      highlights: ['Ramanathaswamy Temple', 'Dhanushkodi', 'Pamban Bridge', 'Adam\'s Bridge'],
      inclusions: ['Accommodation', 'Breakfast', 'Transportation', 'Sightseeing'],
      exclusions: ['Lunch & Dinner', 'Personal Expenses', 'Entry Fees'],
      itinerary: [
        {
          day: 1,
          title: 'Temple Visit and Dhanushkodi',
          activities: ['Ramanathaswamy Temple', 'Dhanushkodi Beach', 'Pamban Bridge']
        }
      ],
      images: ['https://images.pexels.com/photos/3408744/pexels-photo-3408744.jpeg']
    },
    {
      id: '7',
      title: 'Hampi Heritage Explorer',
      destination: 'Hampi, Karnataka',
      category: 'Heritage',
      duration: '3 Days 2 Nights',
      price: '₹11,200',
      status: 'archived',
      bookings: 45,
      rating: 4.8,
      lastModified: '08/07/2025',
      image: 'https://images.pexels.com/photos/3408353/pexels-photo-3408353.jpeg',
      description: 'Explore the ruins of the Vijayanagara Empire with ancient temples and boulder landscapes.',
      highlights: ['Virupaksha Temple', 'Hampi Bazaar', 'Lotus Mahal', 'Elephant Stables'],
      inclusions: ['Accommodation', 'Breakfast', 'Transportation', 'Sightseeing'],
      exclusions: ['Lunch & Dinner', 'Personal Expenses', 'Entry Fees'],
      itinerary: [
        {
          day: 1,
          title: 'Temple Complex and Bazaar',
          activities: ['Virupaksha Temple', 'Hampi Bazaar', 'Sunset at Hemakuta Hill']
        }
      ],
      images: ['https://images.pexels.com/photos/3408353/pexels-photo-3408353.jpeg']
    },
    {
      id: '8',
      title: 'Alleppey Backwater Cruise',
      destination: 'Alleppey, Kerala',
      category: 'Adventure',
      duration: '2 Days 1 Night',
      price: '₹16,800',
      status: 'active',
      bookings: 112,
      rating: 4.6,
      lastModified: '07/07/2025',
      image: 'https://images.pexels.com/photos/1450360/pexels-photo-1450360.jpeg',
      description: 'Experience the Venice of the East with serene backwaters and traditional houseboats.',
      highlights: ['Houseboat Stay', 'Backwater Cruise', 'Village Tour', 'Sunset Views'],
      inclusions: ['Houseboat Accommodation', 'All Meals', 'Cruise', 'Guide'],
      exclusions: ['Personal Expenses', 'Beverages', 'Tips'],
      itinerary: [
        {
          day: 1,
          title: 'Houseboat Experience',
          activities: ['Board houseboat', 'Backwater cruise', 'Village visit']
        }
      ],
      images: ['https://images.pexels.com/photos/1450360/pexels-photo-1450360.jpeg']
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setPackages(mockPackages);
      setLoading(false);
    }, 1000);
  }, []);

  const handleCreatePackage = () => {
    setEditingPackage(null);
    setIsWizardOpen(true);
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setIsWizardOpen(true);
  };

  const handleSavePackage = (packageData) => {
    if (editingPackage) {
      setPackages(prev => prev.map(p => p.id === packageData.id ? packageData : p));
    } else {
      setPackages(prev => [...prev, packageData]);
    }
  };

  const handleDuplicatePackage = (pkg) => {
    const duplicatedPackage = {
      ...pkg,
      id: Date.now().toString(),
      title: `${pkg.title} (Copy)`,
      status: 'draft',
      bookings: 0,
      lastModified: new Date().toLocaleDateString('en-IN')
    };
    setPackages(prev => [...prev, duplicatedPackage]);
  };

  const handleViewBookings = (pkg) => {
    navigate('/payment-processing', { state: { packageId: pkg.id } });
  };

  const handleDeletePackage = (pkg) => {
    if (window.confirm(`Are you sure you want to delete "${pkg.title}"?`)) {
      setPackages(prev => prev.filter(p => p.id !== pkg.id));
    }
  };

  const handleBulkAction = (action, selectedIds) => {
    switch (action) {
      case 'activate':
        setPackages(prev => prev.map(p => 
          selectedIds.includes(p.id) ? { ...p, status: 'active' } : p
        ));
        break;
      case 'suspend':
        setPackages(prev => prev.map(p => 
          selectedIds.includes(p.id) ? { ...p, status: 'suspended' } : p
        ));
        break;
      case 'archive':
        setPackages(prev => prev.map(p => 
          selectedIds.includes(p.id) ? { ...p, status: 'archived' } : p
        ));
        break;
      case 'export':
        console.log('Exporting packages:', selectedIds);
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedIds.length} packages?`)) {
          setPackages(prev => prev.filter(p => !selectedIds.includes(p.id)));
        }
        break;
      default:
        break;
    }
  };

  const handleStatusChange = (packageId, newStatus) => {
    setPackages(prev => prev.map(p => 
      p.id === packageId ? { ...p, status: newStatus } : p
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-muted-foreground">Loading packages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Package Management</h1>
            <p className="text-muted-foreground mt-2">
              Create, edit, and manage travel packages for your customers
            </p>
          </div>
          
          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <Button
              variant="outline"
              onClick={() => setIsAnalyticsOpen(true)}
              iconName="BarChart3"
              iconPosition="left"
            >
              Analytics
            </Button>
            <Button
              variant="default"
              onClick={handleCreatePackage}
              iconName="Plus"
              iconPosition="left"
            >
              Add Package
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <PackageQuickStats packages={packages} />

        {/* Package Table */}
        <PackageTable
          packages={packages}
          onEdit={handleEditPackage}
          onDuplicate={handleDuplicatePackage}
          onViewBookings={handleViewBookings}
          onDelete={handleDeletePackage}
          onBulkAction={handleBulkAction}
          onStatusChange={handleStatusChange}
        />

        {/* Package Creation Wizard */}
        <PackageCreationWizard
          isOpen={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
          onSave={handleSavePackage}
          editingPackage={editingPackage}
        />

        {/* Analytics Sidebar */}
        <AnalyticsSidebar
          isOpen={isAnalyticsOpen}
          onClose={() => setIsAnalyticsOpen(false)}
        />
      </div>
    </div>
  );
};

export default AdminPackageManagement;